import usePermissions from "../../hook/usePermissions";
import Boton from "./Boton";
import { IconoDescargar } from "./Iconos";
import { exportarExcel, exportarPDF } from "./exportarTabla";

/* ============================================================================
   BOTONES DE EXPORTACIÓN · una sola implementación para todo el sistema.

   Quién puede descargar: quien tenga el permiso de exportación del módulo
   (`pacientes.export`, `reportes.export`, `billing.export`, `clients.export`,
   `resultados.export`). Son permisos REALES del catálogo del backend,
   asignables por rol desde Configuración -> Roles: se puede dar la descarga de
   reportes a contabilidad sin convertirla en administradora.

   Fail-closed a propósito: sin `permiso`, o mientras `usePermissions` aún no
   resolvió, los botones salen apagados. Un gate de permisos nunca debe abrirse
   por omisión.

   Por qué apagados y no ocultos: el resto del sistema oculta lo que no se
   puede hacer, pero aquí el usuario pidió explícitamente que se vea que la
   descarga existe y que no está disponible para su perfil. Para que no sea "un
   botón muerto sin explicación" -- lo que el sistema prohíbe --, el motivo va
   en el `title`, que es lo que se lee al pasar el cursor.

   Se exporta SIEMPRE lo que se está viendo (las filas ya filtradas), no el
   listado entero.

   DOS ORÍGENES DE ARCHIVO, un solo componente:
     · por defecto se genera en el navegador desde `filas` (listados);
     · si la pantalla pasa `onExcel` / `onPdf`, se usan esos -- es el caso de
       los reportes, cuyo archivo lo arma el backend y llega como blob.
   ========================================================================== */

export const MOTIVO_SIN_PERMISO = "Tu rol no tiene permiso para descargar este listado";

function BotonesExportar({
    filas,
    campos,
    titulo,
    encabezados,
    permiso,
    onExcel,
    onPdf,
    // Alguna pantalla sólo tiene un formato (el reporte de Producción sólo
    // genera Excel). Se declara en vez de pintar un botón que no lleva a nada.
    formatos = ["excel", "pdf"],
    tamano = "md",
    idPrefijo = "exportar",
    // Alguna pantalla tiene su PROPIA condicion para no poder descargar (la
    // Matriz de Atenciones exige un rango de fechas). Se recibe el motivo, no
    // un booleano, para que el boton apagado siga explicando por que.
    bloqueo = null,
    // Mientras el servidor arma el archivo, el boton lo dice.
    cargando = false,
}) {
    const { hasPermission } = usePermissions();
    const puedeDescargar = Boolean(permiso) && hasPermission(permiso);
    // Con generación en el servidor no hay `filas` que contar: manda la
    // pantalla, que sabe si su consulta trajo algo.
    const generaElServidor = typeof onExcel === "function" || typeof onPdf === "function";
    const sinFilas = !generaElServidor && (!filas || filas.length === 0);

    // Un listado vacío tampoco se puede descargar, y también se dice por qué.
    const motivo = !puedeDescargar
        ? MOTIVO_SIN_PERMISO
        : bloqueo
        ? bloqueo
        : sinFilas
        ? "No hay filas que exportar con los filtros aplicados"
        : null;

    return (
        <>
            {formatos.includes("excel") && (
            <Boton
                tamano={tamano}
                icono={IconoDescargar}
                disabled={!!motivo}
                cargando={cargando}
                titulo={motivo || `Descargar ${titulo} en Excel`}
                onClick={() => (onExcel ? onExcel() : exportarExcel(filas, campos, titulo))}
                data-testid={`${idPrefijo}-excel`}
            >
                Excel
            </Boton>
            )}
            {formatos.includes("pdf") && (
            <Boton
                tamano={tamano}
                icono={IconoDescargar}
                disabled={!!motivo}
                cargando={cargando}
                titulo={motivo || `Descargar ${titulo} en PDF`}
                onClick={() => (onPdf ? onPdf() : exportarPDF(filas, campos, titulo, encabezados))}
                data-testid={`${idPrefijo}-pdf`}
            >
                PDF
            </Boton>
            )}
        </>
    );
}

export default BotonesExportar;
